/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS migration — Connectors. Real triggers for the two connectors this
   migration actually has (ProShip API revenue rollup, HubSpot Activity sync);
   run history is real (growthos_ingest_runs).
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, get, post } from '../common/constants'

const CONNECTORS = apiConstants.GROWTHOS_ADMIN_CONNECTORS
const ROLLUP = apiConstants.GROWTHOS_ADMIN_REVENUE_ROLLUP
const HUBSPOT = apiConstants.GROWTHOS_ADMIN_HUBSPOT
const LINKEDIN_ABM = apiConstants.GROWTHOS_ADMIN_LINKEDIN_ABM

const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return {
      error: {
        status: r?.status,
        data: r?.data,
        message: r?.data?.message || err.message,
      },
    }
  }
}

export const growthosConnectorsApiSlice = createApi({
  reducerPath: 'growthosConnectorsApi',
  baseQuery: () => ({ data: undefined }),
  tagTypes: ['IngestRun', 'ConnectorStatus'],
  endpoints: (b) => ({
    listIngestRuns: b.query({
      queryFn: ({ page, size } = {}) => run(get(`${CONNECTORS}/runs`, { page: page || 0, size: size || 50 })),
      providesTags: ['IngestRun'],
    }),
    // Per-connector last run / last success + untagged-brand count + email-contact universe.
    getConnectorStatus: b.query({
      queryFn: () => run(get(`${CONNECTORS}/status`)),
      providesTags: ['ConnectorStatus'],
    }),
    syncProshipApi: b.mutation({
      queryFn: () => run(post(`${ROLLUP}/sync`)),
      invalidatesTags: ['IngestRun', 'ConnectorStatus'],
    }),
    backfillProshipApi: b.mutation({
      queryFn: (months) => run(post(`${ROLLUP}/backfill?months=${months || 24}`)),
      invalidatesTags: ['IngestRun', 'ConnectorStatus'],
    }),
    // Read-only HubSpot connection test (reference POST /connectors/hubspot/preflight).
    preflightHubspot: b.mutation({
      queryFn: () => run(post(`${HUBSPOT}/preflight`)),
    }),
    syncHubspot: b.mutation({
      queryFn: () => run(post(`${HUBSPOT}/sync`)),
      invalidatesTags: ['IngestRun', 'ConnectorStatus'],
    }),
    // Delta sync — only records changed since the last successful run (falls back to full).
    syncHubspotIncremental: b.mutation({
      queryFn: () => run(post(`${HUBSPOT}/sync?mode=incremental`)),
      invalidatesTags: ['IngestRun', 'ConnectorStatus'],
    }),
    syncLinkedInAbm: b.mutation({
      queryFn: () => run(post(`${LINKEDIN_ABM}/sync`)),
      invalidatesTags: ['IngestRun', 'ConnectorStatus'],
    }),
  }),
})

export const {
  useListIngestRunsQuery,
  useGetConnectorStatusQuery,
  useSyncHubspotIncrementalMutation,
  useSyncProshipApiMutation,
  useBackfillProshipApiMutation,
  useSyncHubspotMutation,
  useSyncLinkedInAbmMutation,
  usePreflightHubspotMutation,
} = growthosConnectorsApiSlice
